import { useState } from "react";
import { Link } from "react-router-dom";
import type { ProductDetail, Product } from "../../../../types/product";
import ReviewSubmitForm from "./ReviewSubmitForm";

interface Props {
  product: Product;
  detail: ProductDetail;
}

type Tab = "overview" | "specs" | "inbox" | "reviews";
type ReviewSort = "newest" | "highest" | "lowest" | "most_helpful";
type StarFilter = 1 | 2 | 3 | 4 | 5 | null;

function StarRating({ rating, small }: { rating: number; small?: boolean }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`${small ? "text-xs" : "text-sm"} ${
            s <= rating ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"
          }`}
        ></i>
      ))}
    </div>
  );
}

export default function ProductTabs({ product, detail }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [reviewSort, setReviewSort] = useState<ReviewSort>("newest");
  const [starFilter, setStarFilter] = useState<StarFilter>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, number>>({});
  const [votedSet, setVotedSet] = useState<Set<string>>(new Set());

  const handleHelpful = (id: string) => {
    if (votedSet.has(id)) return;
    setHelpfulVotes((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
    setVotedSet((prev) => new Set([...prev, id]));
  };

  const toggleStarFilter = (star: 1 | 2 | 3 | 4 | 5) => {
    setStarFilter((prev) => (prev === star ? null : star));
  };

  const filteredReviews = starFilter
    ? detail.reviews.filter((r) => r.rating === starFilter)
    : detail.reviews;

  const sortedReviews = [...filteredReviews].sort((a, b) => {
    if (reviewSort === "highest") return b.rating - a.rating;
    if (reviewSort === "lowest") return a.rating - b.rating;
    if (reviewSort === "most_helpful") {
      return (helpfulVotes[b.id] ?? 0) - (helpfulVotes[a.id] ?? 0);
    }
    // newest — parse date string
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "overview", label: "Overview", icon: "ri-file-text-line" },
    { id: "specs", label: "Specifications", icon: "ri-list-check-2" },
    { id: "inbox", label: "What\u2019s in the Box", icon: "ri-inbox-unarchive-line" },
    { id: "reviews", label: `Reviews (${detail.reviews.length})`, icon: "ri-star-line" },
  ];

  return (
    <div className="mt-12">
      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-gray-200 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer -mb-px ${
              activeTab === tab.id
                ? "border-amber-500 text-amber-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={tab.icon}></i>
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="py-8">

        {/* Overview */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h3 className="font-bold text-gray-900 text-lg mb-4">About This Product</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{detail.description}</p>

              {/* Key specs summary */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {detail.specs.slice(0, 6).map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
                    <p className="text-sm font-bold text-gray-900">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Highlights */}
              {product.highlights.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-bold text-gray-800 text-sm mb-3">Key Features</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {product.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                        </div>
                        <span className="text-sm text-gray-700">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quick ref card */}
            <div className="space-y-4">
              {detail.batteryType && (
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg mb-2">
                    <i className="ri-battery-2-charge-line text-emerald-500"></i>
                  </div>
                  <p className="text-xs text-gray-400 mb-0.5">Battery Chemistry</p>
                  <p className="text-sm font-bold text-gray-900">{detail.batteryType}</p>
                </div>
              )}
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg mb-2">
                  <i className="ri-scales-line text-amber-500"></i>
                </div>
                <p className="text-xs text-gray-400 mb-0.5">Weight</p>
                <p className="text-sm font-bold text-gray-900">{detail.weight}</p>
              </div>
              <Link
                to="/warranty"
                className="block border border-amber-200 bg-amber-50/50 rounded-xl p-4 hover:bg-amber-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className="ri-shield-star-line text-amber-500"></i>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-0.5">Warranty</p>
                <p className="text-sm font-bold text-gray-900">{detail.warranty}</p>
                <p className="text-xs text-amber-600 mt-1 font-medium">View full warranty terms →</p>
              </Link>
              {detail.chargeTime && (
                <div className="border border-gray-100 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg mb-2">
                    <i className="ri-time-line text-amber-500"></i>
                  </div>
                  <p className="text-xs text-gray-400 mb-0.5">Charge Time</p>
                  <p className="text-sm font-bold text-gray-900">{detail.chargeTime}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Specs */}
        {activeTab === "specs" && (
          <div className="max-w-3xl">
            <h3 className="font-bold text-gray-900 text-lg mb-6">Full Specifications</h3>
            <div className="border border-gray-100 rounded-xl overflow-hidden">
              {detail.specs.map((s, i) => (
                <div
                  key={s.label}
                  className={`flex items-start gap-4 px-5 py-3.5 text-sm ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <span className="w-48 shrink-0 text-gray-500 font-medium">{s.label}</span>
                  <span className="text-gray-900 font-semibold">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* In the Box */}
        {activeTab === "inbox" && (
          <div className="max-w-xl">
            <h3 className="font-bold text-gray-900 text-lg mb-6">What&apos;s in the Box</h3>
            <div className="space-y-3">
              {detail.whatsInBox.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-5 py-3.5">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full shrink-0">
                    <span className="text-xs font-extrabold text-amber-600">{i + 1}</span>
                  </div>
                  <span className="text-sm text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              * Accessories are specific to this model. Expansion accessories and solar panels may be sold separately.
            </p>
          </div>
        )}

        {/* Reviews */}
        {activeTab === "reviews" && (
          <div className="max-w-3xl">
            {/* Summary */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8 p-6 bg-gray-50 rounded-xl">
              <div className="text-center">
                <p className="text-5xl font-extrabold text-gray-900">{product.rating.toFixed(1)}</p>
                <div className="flex items-center justify-center mt-1">
                  <StarRating rating={product.rating} />
                </div>
                <p className="text-xs text-gray-400 mt-1">{product.reviews.toLocaleString()} reviews</p>
              </div>
              <div className="flex-1 space-y-2 w-full">
                {[5, 4, 3, 2, 1].map((star) => {
                  const pct = star === 5 ? 72 : star === 4 ? 18 : star === 3 ? 6 : star === 2 ? 3 : 1;
                  const isActive = starFilter === star;
                  return (
                    <button
                      key={star}
                      onClick={() => toggleStarFilter(star as 1 | 2 | 3 | 4 | 5)}
                      className={`flex items-center gap-2 w-full group rounded-lg px-2 py-0.5 transition-all cursor-pointer ${
                        isActive ? "bg-amber-50 ring-1 ring-amber-300" : "hover:bg-gray-100"
                      }`}
                    >
                      <span className={`text-xs w-4 font-semibold ${isActive ? "text-amber-600" : "text-gray-500"}`}>{star}</span>
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`ri-star-fill text-xs ${isActive ? "text-amber-500" : "text-amber-400"}`}></i>
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${isActive ? "bg-amber-500" : "bg-amber-400"}`}
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                      <span className={`text-xs w-7 text-right ${isActive ? "text-amber-600 font-semibold" : "text-gray-400"}`}>{pct}%</span>
                    </button>
                  );
                })}
                {starFilter !== null && (
                  <button
                    onClick={() => setStarFilter(null)}
                    className="flex items-center gap-1 text-xs text-amber-600 font-medium mt-1 hover:text-amber-700 cursor-pointer pl-2"
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-close-circle-line"></i>
                    </div>
                    Clear filter
                  </button>
                )}
              </div>
            </div>

            {/* Star filter pills */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <span className="text-xs text-gray-400 whitespace-nowrap">Filter by stars:</span>
              {([5, 4, 3, 2, 1] as const).map((star) => {
                const isActive = starFilter === star;
                return (
                  <button
                    key={star}
                    onClick={() => toggleStarFilter(star)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
                    }`}
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-star-fill text-xs ${isActive ? "text-white" : "text-amber-400"}`}></i>
                    </div>
                    {star} star{star !== 1 ? "s" : ""}
                  </button>
                );
              })}
              {starFilter !== null && (
                <button
                  onClick={() => setStarFilter(null)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                  All ratings
                </button>
              )}
            </div>

            {/* Sort control */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <p className="text-sm font-semibold text-gray-700">
                {sortedReviews.length}{starFilter ? ` of ${detail.reviews.length}` : ""} review{sortedReviews.length !== 1 ? "s" : ""}
                {starFilter && <span className="ml-1.5 text-amber-600 font-semibold">({starFilter}-star)</span>}
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-gray-400 mr-1 whitespace-nowrap">Sort by:</span>
                {(
                  [
                    { id: "newest", label: "Newest", icon: "ri-time-line" },
                    { id: "highest", label: "Highest Rated", icon: "ri-arrow-up-line" },
                    { id: "lowest", label: "Lowest Rated", icon: "ri-arrow-down-line" },
                    { id: "most_helpful", label: "Most Helpful", icon: "ri-thumb-up-line" },
                  ] as { id: ReviewSort; label: string; icon: string }[]
                ).map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setReviewSort(opt.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                      reviewSort === opt.id
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={opt.icon}></i>
                    </div>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Review list */}
            <div className="space-y-4 mb-8">
              {sortedReviews.length === 0 && (
                <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 text-gray-300 text-2xl">
                    <i className="ri-star-line"></i>
                  </div>
                  <p className="text-sm font-semibold text-gray-500">No {starFilter}-star reviews yet</p>
                  <button
                    onClick={() => setStarFilter(null)}
                    className="mt-3 text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer"
                  >
                    Show all reviews
                  </button>
                </div>
              )}
              {sortedReviews.map((r) => {
                const votes = helpfulVotes[r.id] ?? 0;
                const hasVoted = votedSet.has(r.id);
                return (
                  <div key={r.id} className="border border-gray-100 rounded-xl p-5 transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <StarRating rating={r.rating} />
                          {r.verified && (
                            <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                              <i className="ri-verified-badge-fill"></i> Verified Purchase
                            </span>
                          )}
                        </div>
                        <p className="font-bold text-sm text-gray-900">{r.title}</p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap ml-3">{r.date}</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">{r.body}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">{r.author} &mdash; {r.location}</p>
                      <button
                        onClick={() => handleHelpful(r.id)}
                        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                          hasVoted
                            ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                            : "bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600"
                        }`}
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={hasVoted ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                        </div>
                        Helpful{votes > 0 ? ` (${votes})` : ""}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Submit a review */}
            <div>
              <h4 className="font-bold text-gray-900 text-base mb-3">Share Your Experience</h4>
              <ReviewSubmitForm productName={product.name} productId={product.id} />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
